import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import { GLYPH_STROKES } from "../../../../data/glyphStrokes";
import {
  PopIn,
  ShakeWrap,
  Tile,
  gameShell,
  headerChip,
  hitTestEl,
  pieceButtonProps,
  seededShuffle,
  useCompleteOnce,
  useDragAndTap,
  useWrongFlash,
} from "./engineUtils";

const STROKE_W = 0.085; // glyph units (box is 1x1)

/**
 * Which strokes of which glyph become draggable pieces, per letter config.
 * `pieces` = stroke indices handed to the child; `fixed` = strokes drawn
 * already; `split` = cut the (single) polyline stroke into N segments.
 * Letters without a plan get every uppercase stroke as a piece.
 */
const PLANS = {
  K: [{ glyph: "K", pieces: [0, 1, 2] }],
  L: [{ glyph: "L", pieces: [1], fixed: [0] }, { glyph: "l", pieces: [0] }],
  Q: [{ glyph: "Q", pieces: [1], fixed: [0] }, { glyph: "q", pieces: [1], fixed: [0] }],
  R: [{ glyph: "R", pieces: [0, 1, 2] }, { glyph: "r", pieces: [0, 1] }],
  T: [{ glyph: "T", pieces: [1, 0] }, { glyph: "t", pieces: [0, 1] }],
  V: [{ glyph: "V", split: 2 }],
  W: [{ glyph: "W", split: 4 }],
  Y: [{ glyph: "Y", pieces: [0, 1, 2] }, { glyph: "y", pieces: [0, 1] }],
  Z: [{ glyph: "Z", split: 3 }],
};

function parsePath(d) {
  const tokens = String(d).match(/[MLCQAZ]|-?\d*\.?\d+(?:e-?\d+)?/gi) || [];
  const cmds = [];
  let cur = null;
  tokens.forEach((t) => {
    if (/^[MLCQAZ]$/i.test(t)) { cur = { c: t.toUpperCase(), a: [] }; cmds.push(cur); }
    else if (cur) cur.a.push(Number(t));
  });
  return cmds;
}

function bboxOf(cmds) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const add = (x, y) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
  cmds.forEach(({ c, a }) => {
    if (c === "A") { add(a[5] - a[0], a[6] - a[1]); add(a[5] + a[0], a[6] + a[1]); }
    else for (let i = 0; i + 1 < a.length; i += 2) add(a[i], a[i + 1]);
  });
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 1, h: 1 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Split an M/L polyline into N roughly equal runs of segments. */
function splitPolyline(cmds, n) {
  const pts = [];
  cmds.forEach(({ c, a }) => { if (c === "M" || c === "L") pts.push([a[0], a[1]]); });
  if (pts.length < 2) return [cmds];
  const segs = pts.length - 1;
  const per = Math.max(1, Math.round(segs / n));
  const out = [];
  for (let i = 0; i < segs; i += per) {
    const run = pts.slice(i, Math.min(pts.length, i + per + 1));
    out.push([{ c: "M", a: run[0] }, ...run.slice(1).map((p) => ({ c: "L", a: p }))]);
  }
  return out;
}

const toD = (cmds) => cmds.map(({ c, a }) => `${c} ${a.join(" ")}`).join(" ");

/** Kid-facing name for a straight run: "Slide down", "Slide up", "Line across". */
function runLabel(cmds) {
  const pts = cmds.filter((k) => k.c === "M" || k.c === "L").map((k) => k.a);
  if (pts.length < 2) return "Stroke";
  const [x0, y0] = pts[0];
  const [x1, y1] = pts[pts.length - 1];
  const dx = x1 - x0;
  const dy = y1 - y0;
  if (Math.abs(dy) < 0.08) return "Line across";
  if (Math.abs(dx) < 0.08) return dy > 0 ? "Line down" : "Line up";
  return dy > 0 ? "Slide down" : "Slide up";
}
const pretty = (id) => String(id || "").replace(/[-_]+/g, " ").replace(/^\w/, (m) => m.toUpperCase());

function buildCases(letter, config) {
  const L = String(config.letter || letter || "A").toUpperCase();
  const plan = PLANS[L] || [{ glyph: L, pieces: "all" }];
  const configIds = (config.pieces || []).map((p) => p.id);
  let pieceNo = 0;
  const cases = plan
    .map((p, ci) => {
      const glyph = GLYPH_STROKES[p.glyph];
      if (!glyph?.strokes?.length) return null;
      const strokes = glyph.strokes.map((s) => ({ ...s, cmds: parsePath(s.d) }));
      const pieces = [];
      const fixed = [];
      if (p.split) {
        splitPolyline(strokes[0].cmds, p.split).forEach((cmds, i) => pieces.push({ cmds, prompt: runLabel(cmds), order: i }));
        strokes.slice(1).forEach((s) => fixed.push(s.cmds));
      } else {
        const want = p.pieces === "all" ? strokes.map((_, i) => i) : p.pieces;
        strokes.forEach((s, i) => {
          if (want.includes(i)) pieces.push({ cmds: s.cmds, prompt: s.prompt, order: want.indexOf(i) });
          else if (!p.fixed || p.fixed.includes(i)) fixed.push(s.cmds);
        });
      }
      pieces.sort((a, b) => a.order - b.order);
      return {
        id: `case-${ci}`,
        glyph: p.glyph,
        upper: p.glyph === p.glyph.toUpperCase(),
        hint: glyph.hint,
        guide: strokes.map((s) => toD(s.cmds)),
        fixed: fixed.map(toD),
        pieces: pieces.map((pc) => {
          pieceNo += 1;
          return { id: `${p.glyph}-${pieceNo}`, caseId: `case-${ci}`, d: toD(pc.cmds), bbox: bboxOf(pc.cmds), label: pc.prompt || "stroke" };
        }),
      };
    })
    .filter(Boolean);
  // Config piece ids (stem, bowl, leg…) name the pieces when the counts line up.
  const useIds = configIds.length && configIds.length === pieceNo;
  let k = 0;
  cases.forEach((c) => {
    const bases = c.pieces.map((pc) => { const b = (useIds && pretty(configIds[k])) || pc.label; k += 1; return b; });
    const counts = bases.reduce((m, b) => m.set(b, (m.get(b) || 0) + 1), new Map());
    const seen = new Map();
    c.pieces.forEach((pc, i) => {
      const base = bases[i];
      const n = (seen.get(base) || 0) + 1;
      seen.set(base, n);
      pc.base = base;
      pc.label = `${c.upper ? "Big" : "Little"} ${c.glyph}: ${base}${counts.get(base) > 1 ? ` ${n}` : ""}`;
    });
  });
  return cases;
}

/**
 * Stroke assembly: the letter's strokes are sticker pieces in a tray; drag
 * (or tap, then tap the letter) each one onto its place on the letter card.
 * Right piece draws itself in; wrong piece springs back. Completes when all
 * pieces are placed.
 */
export default function StrokeAssemblyGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const cases = useMemo(() => buildCases(letter, config), [letter, config]);
  const allPieces = useMemo(() => cases.flatMap((c) => c.pieces), [cases]);
  const trayOrder = useMemo(() => seededShuffle(allPieces, 5), [allPieces]);
  const [placed, setPlaced] = useState(() => new Set());
  const [burst, setBurst] = useState(0);
  const spoken = useRef(new Set());
  const targetEls = useRef(new Map()); // piece id -> its own target element
  const total = allPieces.length;

  const place = useCallback(
    (piece, targetId, clientX, clientY) => {
      if (placed.has(piece.id)) return false;
      // Stroke targets overlap (a stem shares its edge with a bowl): a drop that
      // lands inside the piece's OWN target counts even if another one was hovered.
      const ownHit = clientX != null && hitTestEl(clientX, clientY, targetEls.current.get(piece.id));
      if (targetId !== piece.id && !ownHit) {
        flash(piece.id);
        onWrong?.();
        return false;
      }
      sfx?.("snap");
      const next = new Set(placed).add(piece.id);
      setPlaced(next);
      const c = cases.find((x) => x.id === piece.caseId);
      if (c && !spoken.current.has(c.id) && c.pieces.every((p) => next.has(p.id))) {
        spoken.current.add(c.id);
        const L = String(config.letter || letter).toUpperCase();
        speak?.(c.upper ? `Big ${L}!` : `Little ${L.toLowerCase()}!`, { interrupt: true });
      }
      return true;
    },
    [cases, config.letter, flash, letter, onWrong, placed, sfx, speak]
  );

  const onDrop = useCallback(
    ({ payload, targetId, clientX, clientY }) => {
      if (!targetId || !payload) return false;
      return place(payload, targetId, clientX, clientY);
    },
    [place]
  );
  const drag = useDragAndTap({ reduceMotion, sfx, onDrop });

  useEffect(() => {
    if (total > 0 && placed.size >= total) {
      sfx?.("correct");
      setBurst((b) => b + 1);
      const t = setTimeout(finish, reduceMotion ? 0 : 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [placed, total, finish, reduceMotion, sfx]);

  if (!cases.length) return <div style={gameShell.root} />;

  const n = cases.length;
  const cardSize = `min(calc(var(--tile) * 3.6), calc((var(--arena-w) - 40px) / ${n} - 12px), calc(var(--arena-h) - var(--tile) * 1.9 - 40px))`;
  const renderGhost = (p) => <PieceTile piece={p} />;
  const armed = Boolean(drag.picked);

  return (
    <div style={{ ...gameShell.root, gap: compact ? 10 : 16 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={35} reduceMotion={reduceMotion} />
      <div style={{ flex: 1, minHeight: 0, display: "flex", gap: compact ? 10 : 16, alignItems: "center", justifyContent: "center" }}>
        {cases.map((c, ci) => (
          <PopIn key={c.id} index={ci} reduceMotion={reduceMotion}>
            <LetterCard
              c={c}
              size={cardSize}
              placed={placed}
              hoverId={drag.hoverId}
              dragging={drag.dragging || armed}
              registerTarget={(id, el, data) => {
                drag.registerTarget(id, el, data);
                if (el) targetEls.current.set(id, el); else targetEls.current.delete(id);
              }}
              onTapTarget={(id) => drag.tapTarget(id, { piece: id })}
              armed={armed}
              reduceMotion={reduceMotion}
            />
          </PopIn>
        ))}
      </div>
      <div style={{ ...gameShell.tray, flexShrink: 0 }} aria-label="Letter pieces">
        <AnimatePresence initial={false}>
          {trayOrder.filter((p) => !placed.has(p.id)).map((p, i) => (
            <PopIn key={p.id} index={i + n} reduceMotion={reduceMotion} layout exit={reduceMotion ? undefined : { scale: 0.3, opacity: 0, transition: { duration: 0.14 } }}>
              <ShakeWrap shake={wrongId === p.id} reduceMotion={reduceMotion} style={{ borderRadius: "calc(var(--tile) * 0.24)" }}>
                <button
                  {...pieceButtonProps({
                    dragProps: drag.dragProps,
                    payload: p,
                    render: renderGhost,
                    label: p.label,
                    onTap: () => { sfx?.("pop", { volume: 0.5 }); speak?.(p.base || "Stroke", { interrupt: true }); drag.pick(p.id, p); },
                  })}
                  aria-pressed={drag.isPicked(p.id)}
                >
                  <PieceTile piece={p} selected={drag.isPicked(p.id)} />
                </button>
              </ShakeWrap>
            </PopIn>
          ))}
        </AnimatePresence>
      </div>
      <drag.GhostLayer />
    </div>
  );
}

/** A stroke piece drawn large inside a sticker tile, orientation preserved. */
function PieceTile({ piece, selected = false }) {
  const b = piece.bbox;
  const side = Math.max(b.w, b.h, 0.42) + STROKE_W * 2.2;
  const vx = b.x + b.w / 2 - side / 2;
  const vy = b.y + b.h / 2 - side / 2;
  return (
    <Tile selected={selected} accent={LEARN.color.blue}>
      <svg viewBox={`${vx} ${vy} ${side} ${side}`} width="82%" height="82%" aria-hidden="true" style={{ overflow: "visible" }}>
        <path d={piece.d} fill="none" stroke={LEARN.color.blueDeep} strokeWidth={STROKE_W * 1.15} strokeLinecap="round" strokeLinejoin="round" opacity={0.35} transform={`translate(0 ${STROKE_W * 0.35})`} />
        <path d={piece.d} fill="none" stroke={LEARN.color.blue} strokeWidth={STROKE_W * 1.15} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Tile>
  );
}

function LetterCard({ c, size, placed, hoverId, dragging, registerTarget, onTapTarget, armed, reduceMotion }) {
  const done = c.pieces.every((p) => placed.has(p.id));
  const chip = headerChip({ accent: LEARN.color.blue, deep: LEARN.color.blueDeep, compact: true });
  return (
    <div
      style={{
        width: size, height: size, position: "relative", flexShrink: 0,
        background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        borderRadius: LEARN.radius.lg, boxShadow: done ? `0 0 0 4px ${LEARN.color.mint}77, ${LEARN.shadow.float}` : LEARN.shadow.card,
        border: `2px solid ${done ? LEARN.color.mint : LEARN.color.line}`,
        transition: "box-shadow 240ms ease, border-color 240ms ease",
      }}
      aria-label={c.hint}
    >
      <svg viewBox="0 0 1 1" width="100%" height="100%" aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "visible" }}>
        {/* Writing lines */}
        <line x1="0.06" y1="0.12" x2="0.94" y2="0.12" stroke="rgba(11,26,51,0.08)" strokeWidth="0.006" />
        <line x1="0.06" y1="0.42" x2="0.94" y2="0.42" stroke="rgba(11,26,51,0.08)" strokeWidth="0.006" strokeDasharray="0.02 0.015" />
        <line x1="0.06" y1="0.88" x2="0.94" y2="0.88" stroke="rgba(11,26,51,0.12)" strokeWidth="0.008" />
        {/* Ghost guide of the whole letter */}
        {c.guide.map((d, i) => (
          <path key={`g-${i}`} d={d} fill="none" stroke="rgba(11,26,51,0.10)" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
        ))}
        {c.fixed.map((d, i) => (
          <path key={`f-${i}`} d={d} fill="none" stroke={LEARN.color.blue} strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
        ))}
        {c.pieces.map((p) => {
          const isPlaced = placed.has(p.id);
          const hover = hoverId === p.id && !isPlaced;
          return (
            <g key={p.id}>
              {!isPlaced && (dragging || armed) && (
                <path d={p.d} fill="none" stroke={hover ? LEARN.color.sun : `${LEARN.color.sun}88`} strokeWidth={STROKE_W * (hover ? 1.5 : 1.05)} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={hover ? undefined : "0.03 0.03"} opacity={hover ? 0.95 : 0.8} style={{ transition: "stroke-width 120ms ease" }} />
              )}
              {isPlaced && (
                <motion.path
                  d={p.d}
                  fill="none"
                  stroke={done ? LEARN.color.mint : LEARN.color.blue}
                  strokeWidth={STROKE_W}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={reduceMotion ? false : { pathLength: 0, opacity: 0.4 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  style={{ transition: "stroke 300ms ease" }}
                />
              )}
            </g>
          );
        })}
      </svg>
      {/* Drop targets over each missing piece */}
      {c.pieces.map((p) => {
        if (placed.has(p.id)) return null;
        const pad = 0.09;
        const w = Math.max(p.bbox.w + pad * 2, 0.28);
        const h = Math.max(p.bbox.h + pad * 2, 0.28);
        const cx = p.bbox.x + p.bbox.w / 2;
        const cy = p.bbox.y + p.bbox.h / 2;
        return (
          <div
            key={`t-${p.id}`}
            ref={(el) => registerTarget(p.id, el, { piece: p.id })}
            role={armed ? "button" : undefined}
            tabIndex={armed ? 0 : -1}
            aria-label={`Place ${p.label}`}
            onClick={armed ? () => onTapTarget(p.id) : undefined}
            onKeyDown={(e) => { if (armed && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onTapTarget(p.id); } }}
            style={{
              position: "absolute",
              left: `${(cx - w / 2) * 100}%`,
              top: `${(cy - h / 2) * 100}%`,
              width: `${w * 100}%`,
              height: `${h * 100}%`,
              borderRadius: 18,
              cursor: armed ? "pointer" : "default",
              background: hoverId === p.id ? `${LEARN.color.sun}1f` : "transparent",
              transition: "background 120ms ease",
            }}
          />
        );
      })}
      <span aria-hidden="true" style={{ position: "absolute", left: 10, top: 10, ...chip.chip, background: done ? `${LEARN.color.mint}22` : chip.chip.background, borderColor: done ? `${LEARN.color.mint}55` : chip.chip.borderColor, color: done ? LEARN.color.mintDeep : chip.chip.color, transition: "background 200ms ease, color 200ms ease" }}>
        <span style={{ ...chip.badge, background: done ? LEARN.color.mint : chip.badge.background, boxShadow: `0 2px 0 ${done ? LEARN.color.mintDeep : LEARN.color.blueDeep}` }}>{c.glyph}</span>
        <span style={chip.title}>{c.upper ? "Big" : "Little"}</span>
      </span>
    </div>
  );
}
